/**
 * The storage boundary between the two clients.
 *
 * Its shape is deliberately the one ProjectRepository/ChatRepository already have, so the
 * mobile implementation is those objects passed through unchanged, the browser supplies
 * an IndexedDB implementation, and the forthcoming /me/projects service supplies a third
 * without either client's screens knowing. See
 * docs/superpowers/specs/2026-09-09-saddle-web-client-design.md §3.
 */
import type { Project } from './ProjectRepository';
import type { ChatMessage } from '@/llm/types';
import type { AppDefinition } from '@/types/app';

export type ProjectUpdate = Partial<
  Pick<Project, 'name' | 'description' | 'definition' | 'thumbnail' | 'is_published'>
>;

export interface NewProject {
  name: string;
  description?: string;
  definition: AppDefinition;
}

export interface ProjectStore {
  claimUnowned(ownerId: string): Promise<void>;
  getAll(ownerId: string): Promise<Project[]>;
  getById(id: string, ownerId: string): Promise<Project | null>;
  create(project: NewProject, ownerId: string): Promise<Project>;
  update(id: string, updates: ProjectUpdate, ownerId: string): Promise<void>;
  updateName(id: string, name: string, ownerId: string): Promise<void>;
  delete(id: string, ownerId: string): Promise<void>;
  duplicate(id: string, ownerId: string): Promise<Project>;
}

export interface ChatStore {
  getByProjectId(projectId: string): Promise<ChatMessage[]>;
  addMessage(projectId: string, message: Omit<ChatMessage, 'id'>): Promise<ChatMessage>;
  deleteByProjectId(projectId: string): Promise<void>;
}

export interface Stores {
  projects: ProjectStore;
  chat: ChatStore;
}

let stores: Stores | null = null;

/** Called once at startup: src/app/_layout.tsx on mobile, web/src/bootstrap.ts in the browser. */
export function setStores(next: Stores): void {
  stores = next;
}

function required(): Stores {
  if (!stores) {
    throw new Error('setStores() was never called — the app has no storage wired up.');
  }
  return stores;
}

export const projectStore = (): ProjectStore => required().projects;
export const chatStore = (): ChatStore => required().chat;

/** Test helper: forget the registration so a suite starts from a known state. */
export function resetStores(): void {
  stores = null;
}
